import React, { useState } from "react";
import { useCart } from "../contexts/CartContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Minus, Plus, Gift, Sparkles, Check } from "lucide-react";

const KIDS_PRICE = 6.9;
const KIDS_IMAGE = "https://images.unsplash.com/photo-1586190848861-99aa4a171e90";

const DRINKS = [
    { id: "coca", label: "Coca Cola", emoji: "🥤" },
    { id: "fanta", label: "Fanta", emoji: "🍊" },
    { id: "sprite", label: "Sprite", emoji: "🍋" },
    { id: "juice", label: "Χυμός", emoji: "🧃" },
];
const SAUCES = [
    { id: "ketchup", label: "Ketchup", emoji: "🍅" },
    { id: "mayo", label: "Mayo", emoji: "🥚" },
    { id: "bbq", label: "BBQ", emoji: "🔥" },
    { id: "honey", label: "Honey Mustard", emoji: "🍯" },
];
const GIFTS = [
    { id: "car", label: "Αυτοκινητάκι", emoji: "🚗" },
    { id: "dino", label: "Δεινόσαυρος", emoji: "🦖" },
    { id: "puzzle", label: "Puzzle", emoji: "🧩" },
    { id: "keychain", label: "Μπρελόκ", emoji: "🔑" },
];

const OptionCard = ({ selected, onClick, emoji, label, testId }) => (
    <button
        type="button"
        data-testid={testId}
        onClick={onClick}
        className={`relative flex items-center gap-3 p-3 rounded-2xl border-2 transition-all duration-200 text-left overflow-hidden ${
            selected
                ? "border-brand-red bg-gradient-to-br from-brand-red/15 to-brand-yellow/10 shadow-lg shadow-brand-red/20 scale-[1.02]"
                : "border-white/15 bg-white/5 hover:border-white/30 hover:bg-white/10"
        }`}
    >
        <span className="text-3xl sm:text-4xl shrink-0" aria-hidden>{emoji}</span>
        <span className="font-heading uppercase tracking-wider text-sm">{label}</span>
        {selected && (
            <span className="absolute top-2 right-2 h-5 w-5 grid place-items-center rounded-full bg-brand-red text-white">
                <Check size={12} strokeWidth={3} />
            </span>
        )}
    </button>
);

export const KidsMenu = () => {
    const { add } = useCart();
    const navigate = useNavigate();
    const [drink, setDrink] = useState(DRINKS[0].id);
    const [sauce, setSauce] = useState(SAUCES[0].id);
    const [gift, setGift] = useState(GIFTS[0].id);
    const [qty, setQty] = useState(1);

    const handleAdd = () => {
        const d = DRINKS.find((x) => x.id === drink);
        const s = SAUCES.find((x) => x.id === sauce);
        const g = GIFTS.find((x) => x.id === gift);
        const id = `kids-${drink}-${sauce}-${gift}`;
        const name = `Kids Menu · ${d.label} · ${s.label} · ${g.label}`;
        for (let i = 0; i < qty; i++) {
            add({ id, name, price: KIDS_PRICE, image_url: KIDS_IMAGE });
        }
        toast.success(`${qty}× Kids Menu προστέθηκε!`);
    };

    return (
        <div
            data-testid="kids-menu-page"
            className="relative overflow-hidden min-h-[calc(100vh-4rem)]"
        >
            {/* Festive background */}
            <div className="absolute inset-0 -z-10 opacity-30">
                <div className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-brand-red blur-3xl" />
                <div className="absolute top-40 -right-20 w-96 h-96 rounded-full bg-brand-yellow blur-3xl" />
                <div className="absolute bottom-0 left-1/3 w-72 h-72 rounded-full bg-brand-red/70 blur-3xl" />
            </div>

            <div className="mx-auto max-w-7xl px-4 sm:px-8 py-12">
                <div className="grid lg:grid-cols-[1fr_1.1fr] gap-8 items-start">
                    {/* LEFT — info + options */}
                    <div className="space-y-6">
                        <div>
                            <span className="inline-flex items-center gap-2 bg-gradient-to-r from-brand-yellow to-amber-400 text-black px-4 py-1.5 rounded-full text-xs font-heading uppercase tracking-widest font-black shadow-lg animate-pulse">
                                <Gift size={14} /> Δώρο Έκπληξη!
                            </span>
                            <h1 className="headline text-5xl sm:text-6xl lg:text-7xl mt-4">
                                <span className="text-brand-red">KIDS</span><br />
                                <span className="text-brand-yellow">MENU</span>
                            </h1>
                            <p className="text-white/80 mt-4 text-base sm:text-lg max-w-lg leading-relaxed">
                                Μερίδα με <strong>κοτομπουκιές</strong>, <strong>πατάτες</strong> και <strong>αναψυκτικό</strong> της επιλογής σου — συν ένα μικρό δωράκι για χαρά!
                            </p>
                        </div>

                        {/* Drink */}
                        <section data-testid="kids-drinks">
                            <h2 className="font-heading uppercase tracking-wider text-sm text-brand-yellow mb-3 flex items-center gap-2">
                                <Sparkles size={14} /> Διάλεξε Ρόφημα
                            </h2>
                            <div className="grid grid-cols-2 gap-3">
                                {DRINKS.map((d) => (
                                    <OptionCard
                                        key={d.id}
                                        selected={drink === d.id}
                                        onClick={() => setDrink(d.id)}
                                        emoji={d.emoji}
                                        label={d.label}
                                        testId={`kids-drink-${d.id}`}
                                    />
                                ))}
                            </div>
                        </section>

                        {/* Sauce */}
                        <section data-testid="kids-sauces">
                            <h2 className="font-heading uppercase tracking-wider text-sm text-brand-yellow mb-3 flex items-center gap-2">
                                <Sparkles size={14} /> Διάλεξε Σάλτσα
                            </h2>
                            <div className="grid grid-cols-2 gap-3">
                                {SAUCES.map((s) => (
                                    <OptionCard
                                        key={s.id}
                                        selected={sauce === s.id}
                                        onClick={() => setSauce(s.id)}
                                        emoji={s.emoji}
                                        label={s.label}
                                        testId={`kids-sauce-${s.id}`}
                                    />
                                ))}
                            </div>
                        </section>

                        {/* Gift */}
                        <section data-testid="kids-gifts">
                            <h2 className="font-heading uppercase tracking-wider text-sm text-brand-yellow mb-3 flex items-center gap-2">
                                <Gift size={14} /> Διάλεξε το Δώρο σου
                            </h2>
                            <div className="grid grid-cols-2 gap-3">
                                {GIFTS.map((g) => (
                                    <OptionCard
                                        key={g.id}
                                        selected={gift === g.id}
                                        onClick={() => setGift(g.id)}
                                        emoji={g.emoji}
                                        label={g.label}
                                        testId={`kids-gift-${g.id}`}
                                    />
                                ))}
                            </div>
                        </section>
                    </div>

                    {/* RIGHT — image + CTA */}
                    <aside className="lg:sticky lg:top-20">
                        <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-brand-red via-red-500 to-brand-yellow p-6 sm:p-8 shadow-2xl shadow-brand-red/30">
                            {/* Confetti dots */}
                            <div className="absolute inset-0 pointer-events-none opacity-40">
                                {[...Array(18)].map((_, i) => (
                                    <span
                                        key={i}
                                        className="absolute h-2 w-2 rounded-full bg-white"
                                        style={{
                                            top: `${Math.random() * 100}%`,
                                            left: `${Math.random() * 100}%`,
                                            opacity: 0.4 + Math.random() * 0.6,
                                        }}
                                    />
                                ))}
                            </div>

                            <div className="relative z-10 aspect-square rounded-2xl overflow-hidden bg-black/20 border-4 border-white/30 shadow-xl">
                                <img
                                    src={KIDS_IMAGE}
                                    alt="Kids Menu"
                                    className="w-full h-full object-cover"
                                />
                                <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-white text-brand-red px-3 py-1.5 rounded-full text-[11px] font-heading uppercase tracking-widest font-black shadow-md">
                                    🎁 Surprise Gift
                                </span>
                            </div>

                            <div className="relative z-10 mt-6 bg-white rounded-2xl p-5 sm:p-6 text-black shadow-xl">
                                {/* Selection summary */}
                                <div className="flex flex-wrap gap-2 mb-4 text-xs">
                                    <span className="bg-brand-red/10 text-brand-red px-2 py-1 rounded-full font-bold uppercase tracking-wider">
                                        {DRINKS.find((d) => d.id === drink)?.emoji} {DRINKS.find((d) => d.id === drink)?.label}
                                    </span>
                                    <span className="bg-brand-yellow/20 text-amber-700 px-2 py-1 rounded-full font-bold uppercase tracking-wider">
                                        {SAUCES.find((s) => s.id === sauce)?.emoji} {SAUCES.find((s) => s.id === sauce)?.label}
                                    </span>
                                    <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-bold uppercase tracking-wider">
                                        {GIFTS.find((g) => g.id === gift)?.emoji} {GIFTS.find((g) => g.id === gift)?.label}
                                    </span>
                                </div>

                                <div className="flex items-center justify-between gap-4">
                                    <div className="flex items-center gap-2 border-2 border-black/10 rounded-full p-1 bg-gray-50">
                                        <button
                                            data-testid="kids-qty-dec"
                                            onClick={() => setQty((q) => Math.max(1, q - 1))}
                                            className="h-9 w-9 grid place-items-center rounded-full hover:bg-white active:scale-90 transition"
                                        >
                                            <Minus size={16} strokeWidth={3} />
                                        </button>
                                        <span data-testid="kids-qty" className="w-8 text-center font-heading font-black text-lg">{qty}</span>
                                        <button
                                            data-testid="kids-qty-inc"
                                            onClick={() => setQty((q) => q + 1)}
                                            className="h-9 w-9 grid place-items-center rounded-full hover:bg-white active:scale-90 transition"
                                        >
                                            <Plus size={16} strokeWidth={3} />
                                        </button>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-[10px] uppercase tracking-widest text-gray-500">Σύνολο</div>
                                        <div data-testid="kids-total" className="font-heading font-black text-3xl text-brand-red leading-none">
                                            €{(KIDS_PRICE * qty).toFixed(2)}
                                        </div>
                                    </div>
                                </div>

                                <button
                                    data-testid="kids-add-btn"
                                    onClick={handleAdd}
                                    className="mt-5 w-full h-14 flex items-center justify-center gap-2 bg-gradient-to-r from-brand-red to-red-500 hover:from-brand-redhover hover:to-red-600 text-white font-heading uppercase tracking-widest text-base font-black rounded-full transition-all hover:-translate-y-0.5 active:scale-95 shadow-lg shadow-brand-red/30"
                                >
                                    Το Θέλω! 🎉
                                </button>
                                <button
                                    onClick={() => navigate("/menu")}
                                    className="mt-2 w-full h-10 text-xs uppercase tracking-widest font-heading font-bold text-gray-500 hover:text-brand-red transition"
                                >
                                    ← Πίσω στο μενού
                                </button>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
};
